import { FUELS, type Fuel, type FuelStation } from "./model";
export const MAX_FUEL_FAVORITES = 30;
/** Keep the existing `fuel` link for My Stations; onboarding opens the map. */
export const FUEL_MAP_START_PARAMETER = "fuel_map";
export const isStationId = (value: unknown): value is string => typeof value === "string" && /^md_[a-f0-9]{24}$/.test(value);
export interface FuelFavorite { station: FuelStation; fuels: Fuel[] }
export interface FuelFavoritesState { revision: number; favorites: FuelFavorite[] }
export interface FuelFavoritesAPI {
  load(): Promise<FuelFavoritesState>;
  save(stationId: string, fuels: Fuel[], saved: boolean, revision: number): Promise<FuelFavoritesState>;
}
export function validFuels(value: unknown): value is Fuel[] {
  return Array.isArray(value) && value.length <= 3 && new Set(value).size === value.length && value.every(f => FUELS.includes(f));
}
export function fuelStartParameter(id: string): string { return isStationId(id) ? `fuel_${id}` : "fuel"; }
export function fuelSettingsStartParameter(id: string): string { return isStationId(id) ? `fuela_${id}` : "fuel"; }
export function parseFuelSettingsStartParameter(value: string | null | undefined): string | null {
  const id = value?.startsWith("fuela_") ? value.slice(6) : null;
  return isStationId(id) ? id : null;
}
export function parseFuelStartParameter(value: string | null | undefined): string | null {
  if (value === "fuel" || value === FUEL_MAP_START_PARAMETER) return "";
  const id = value?.startsWith("fuel_") ? value.slice(5) : null;
  return isStationId(id) ? id : null;
}
export function parseFuelNavigation(value: string | null | undefined): { stationId: string; mine: boolean; settings: boolean } | null {
  const settingsId = parseFuelSettingsStartParameter(value);
  const stationId = parseFuelStartParameter(value);
  if (settingsId === null && stationId === null) return null;
  return { stationId: settingsId ?? stationId ?? "", mine: value === "fuel", settings: settingsId !== null };
}
export function createFuelFavoritesAPI(initData: () => string, locale: () => string): FuelFavoritesAPI {
  async function request(action: string, data = {}): Promise<FuelFavoritesState> {
    const auth = initData(); if (!auth) throw new Error("unauthorized");
    const response = await fetch("/api/fuel-favorites", { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ initData: auth, locale: locale(), action, ...data }), signal: AbortSignal.timeout(15_000) });
    const body = await response.json() as FuelFavoritesState & { error?: string };
    if (!response.ok) throw new Error(body.error || "fuel_unavailable");
    if (!Number.isSafeInteger(body.revision) || !Array.isArray(body.favorites)) throw new Error("fuel_unavailable");
    return body;
  }
  return { load: () => request("list"), save: (stationId, fuels, saved, revision) => request("save", { stationId, fuels, saved, revision }) };
}
